const DRAWING_DIM_FONT_PX = 11;
const DRAWING_DIM_FONT_PX_COMPACT = 10;

function formatDrawingDimension(value) {
  const num = Math.round(Number(value));
  if (!num || Number.isNaN(num)) return '';
  return String(num);
}

function stripMmFromLabel(raw) {
  return String(raw || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*mm\s*$/i, '')
    .trim();
}

function isPerPartForeignObject(foreignObject) {
  const w = parseFloat(foreignObject.getAttribute('width') || '0');
  return w > 0 && w <= 55;
}

function isMainDimensionForeignObject(foreignObject) {
  if (isPerPartForeignObject(foreignObject)) return false;
  if (
    Array.from(foreignObject.querySelectorAll('span')).some((span) =>
      (span.textContent || '').toLowerCase().includes('mm')
    )
  ) {
    return true;
  }
  const width = parseFloat(foreignObject.getAttribute('width') || '0');
  return width >= 90 && !!foreignObject.querySelector('input[type="number"]');
}

function getForeignObjectLabel(foreignObject) {
  const input = foreignObject.querySelector('input');
  const value = (input?.getAttribute('value') || input?.value || '').trim();
  if (value) return formatDrawingDimension(value);

  const spans = Array.from(foreignObject.querySelectorAll('span'))
    .map((span) => span.textContent?.trim())
    .filter(Boolean)
    .filter((text) => text.toLowerCase() !== 'mm');
  if (spans.length) return formatDrawingDimension(spans.join(' '));

  return formatDrawingDimension(stripMmFromLabel(foreignObject.textContent));
}

function isDimensionLabelText(textEl) {
  if (textEl.getAttribute('data-dimension-label') === 'true') return true;
  const content = stripMmFromLabel(textEl.textContent);
  if (!content) return false;
  return /^\d+$/.test(content);
}

function getTextLabel(textEl) {
  return formatDrawingDimension(stripMmFromLabel(textEl.textContent));
}

function inferLabelAxis(element) {
  if (!element) return 'width';
  if (element.getAttribute?.('data-dim-band') === 'top') return 'width';
  // Left side-light height takes priority over a stale data-dim-axis="width"
  if (element.getAttribute?.('data-height-side') === 'left') return 'height';
  if (element.getAttribute?.('text-anchor') === 'end') return 'height';

  const axisAttr = element.getAttribute?.('data-dim-axis');
  if (axisAttr === 'height') return 'height';
  // Do not early-return width yet — may still be a mis-tagged left height label

  if (element.getAttribute?.('data-height-part') === '1') return 'height';

  if (element.tagName === 'text') {
    const svg = element.ownerSVGElement || element.closest?.('svg');
    // Top-light overhead widths (500/500) stay width even if near a left gutter line
    if (svg && isTopBandWidthLabel(element, svg)) return 'width';
    const anchor = element.getAttribute('text-anchor') || 'middle';
    if (anchor === 'end' || anchor === 'start') return 'height';
    // Recover older saved SVGs that marked left height as width
    if (axisAttr === 'width' && isLeftHeightElement(element)) return 'height';
    if (axisAttr === 'width') return 'width';
    return 'width';
  }

  if (element.tagName === 'foreignObject') {
    const input = element.querySelector('input');
    const style = input?.getAttribute?.('style') || input?.style?.cssText || '';
    if (/text-align\s*:\s*left/i.test(style)) return 'height';

    // Section height labels sit on the right of the door (no input)
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    const w = parseFloat(element.getAttribute('width') || '0');
    const svg = element.ownerSVGElement || element.closest?.('svg');
    if (svg && w > 0 && w <= 90) {
      const { x: vbX, width: vbW } = getSvgViewBox(svg);
      const frameBottom = estimateOuterFrameBottom(svg);
      let frameRight = null;
      let frameLeft = null;
      let frameTop = null;
      svg.querySelectorAll('rect').forEach((rect) => {
        const rw = parseFloat(rect.getAttribute('width') || '0');
        const rh = parseFloat(rect.getAttribute('height') || '0');
        const rx = parseFloat(rect.getAttribute('x') || '0');
        const ry = parseFloat(rect.getAttribute('y') || '0');
        if (rw < 60 || rh < 60) return;
        if (rw >= vbW * 0.92) return;
        const fill = (rect.getAttribute('fill') || '').toLowerCase();
        if (fill === '#f5f5f5') return;
        const right = rx + rw;
        if (frameRight == null || right > frameRight) frameRight = right;
        if (frameLeft == null || rx < frameLeft) frameLeft = rx;
        if (frameTop == null || ry < frameTop) frameTop = ry;
      });
      // Left of outer frame = side-light height labels (achterdeur)
      if (
        frameLeft != null &&
        x + w <= frameLeft + 4 &&
        x < vbX + vbW * 0.45 &&
        (frameTop == null || y >= frameTop + 10) &&
        (frameBottom == null || y <= frameBottom + 6)
      ) {
        return 'height';
      }
      if (frameRight != null && x + 2 < frameRight) {
        // over the glass / sash — width label
      } else if (frameTop != null && y < frameTop + 10) {
        // top overhead width band
      } else if (x > vbX + vbW * 0.45 && (frameBottom == null || y <= frameBottom + 6)) {
        return 'height';
      }
    }
  }

  if (axisAttr === 'width') return 'width';
  return 'width';
}

function svgPointToWrap(svgEl, wrapEl, svgX, svgY) {
  if (!svgEl || !wrapEl) return null;

  const pt = svgEl.createSVGPoint();
  pt.x = svgX;
  pt.y = svgY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return null;

  const screen = pt.matrixTransform(ctm);
  const wrapRect = wrapEl.getBoundingClientRect();
  // Convert viewport px → wrap-local CSS px (handles QuotePreviewScaler transform: scale)
  const scaleX = wrapRect.width > 0 ? wrapEl.offsetWidth / wrapRect.width : 1;
  const scaleY = wrapRect.height > 0 ? wrapEl.offsetHeight / wrapRect.height : 1;
  return {
    left: (screen.x - wrapRect.left) * scaleX,
    top: (screen.y - wrapRect.top) * scaleY,
  };
}

/** Map SVG user units to wrap pixels when the SVG is shown as an <img> (object-contain / meet). */
function getSvgMeetTransform(svgEl, imgEl) {
  const viewBox = svgEl.viewBox?.baseVal;
  const minX = viewBox?.x || 0;
  const minY = viewBox?.y || 0;
  const vbWidth =
    viewBox?.width ||
    parseFloat(svgEl.getAttribute('width')) ||
    imgEl.naturalWidth ||
    1;
  const vbHeight =
    viewBox?.height ||
    parseFloat(svgEl.getAttribute('height')) ||
    imgEl.naturalHeight ||
    1;
  const imgWidth = imgEl.clientWidth;
  const imgHeight = imgEl.clientHeight;
  const scale = Math.min(imgWidth / vbWidth, imgHeight / vbHeight);
  const renderedWidth = vbWidth * scale;
  const renderedHeight = vbHeight * scale;

  return {
    minX,
    minY,
    vbWidth,
    vbHeight,
    scale,
    padX: (imgWidth - renderedWidth) / 2,
    padY: (imgHeight - renderedHeight) / 2,
    renderedWidth,
    renderedHeight,
  };
}

function svgPointToImageWrap(svgEl, imgEl, wrapEl, svgX, svgY) {
  if (!svgEl || !imgEl || !wrapEl || imgEl.clientWidth < 1) return null;

  const wrapRect = wrapEl.getBoundingClientRect();
  const imgRect = imgEl.getBoundingClientRect();
  // getBoundingClientRect is viewport-scaled; clientWidth/pad are local CSS px
  const scaleX = wrapRect.width > 0 ? wrapEl.offsetWidth / wrapRect.width : 1;
  const scaleY = wrapRect.height > 0 ? wrapEl.offsetHeight / wrapRect.height : 1;
  const { minX, minY, scale, padX, padY } = getSvgMeetTransform(svgEl, imgEl);

  return {
    left: (imgRect.left - wrapRect.left) * scaleX + padX + (svgX - minX) * scale,
    top: (imgRect.top - wrapRect.top) * scaleY + padY + (svgY - minY) * scale,
  };
}

function getSvgViewBox(svgEl) {
  const vb = svgEl.viewBox?.baseVal;
  if (vb && vb.width > 0 && vb.height > 0) {
    return { x: vb.x || 0, y: vb.y || 0, width: vb.width, height: vb.height };
  }
  const attr = svgEl.getAttribute('viewBox');
  if (attr) {
    const parts = attr.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => !Number.isNaN(n))) {
      return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
    }
  }
  return {
    x: 0,
    y: 0,
    width: parseFloat(svgEl.getAttribute('width')) || 800,
    height: parseFloat(svgEl.getAttribute('height')) || 800,
  };
}

/** Bottom of the outer door/window frame (ignore canvas-sized background rects). */
function estimateOuterFrameBottom(svgEl) {
  if (!svgEl) return null;

  const { width: vbW, height: vbH } = getSvgViewBox(svgEl);
  let bestBottom = null;

  svgEl.querySelectorAll('rect').forEach((rect) => {
    const w = parseFloat(rect.getAttribute('width') || '0');
    const h = parseFloat(rect.getAttribute('height') || '0');
    const y = parseFloat(rect.getAttribute('y') || '0');
    if (w < 60 || h < 60) return;
    // Full-canvas background only
    if (w >= vbW * 0.92 && h >= vbH * 0.92) return;
    const fill = (rect.getAttribute('fill') || '').toLowerCase();
    if (fill === '#f5f5f5') return;

    // Prefer the lowest sill (outer frame), including white/light outer frames
    const bottom = y + h;
    if (bestBottom == null || bottom > bestBottom) {
      bestBottom = bottom;
    }
  });

  return bestBottom;
}

/** Top of the outer door/window frame. */
function estimateOuterFrameTop(svgEl) {
  if (!svgEl) return null;

  const { width: vbW, height: vbH } = getSvgViewBox(svgEl);
  let bestTop = null;

  svgEl.querySelectorAll('rect').forEach((rect) => {
    const w = parseFloat(rect.getAttribute('width') || '0');
    const h = parseFloat(rect.getAttribute('height') || '0');
    const y = parseFloat(rect.getAttribute('y') || '0');
    if (w < 60 || h < 60) return;
    if (w >= vbW * 0.92 && h >= vbH * 0.92) return;
    const fill = (rect.getAttribute('fill') || '').toLowerCase();
    if (fill === '#f5f5f5') return;
    if (bestTop == null || y < bestTop) bestTop = y;
  });

  return bestTop;
}

/** Left edge of the outer door/window frame. */
function estimateOuterFrameLeft(svgEl) {
  if (!svgEl) return null;

  const { width: vbW, height: vbH } = getSvgViewBox(svgEl);
  let bestLeft = null;

  svgEl.querySelectorAll('rect').forEach((rect) => {
    const w = parseFloat(rect.getAttribute('width') || '0');
    const h = parseFloat(rect.getAttribute('height') || '0');
    const x = parseFloat(rect.getAttribute('x') || '0');
    if (w < 60 || h < 60) return;
    if (w >= vbW * 0.92 && h >= vbH * 0.92) return;
    const fill = (rect.getAttribute('fill') || '').toLowerCase();
    if (fill === '#f5f5f5') return;
    if (bestLeft == null || x < bestLeft) bestLeft = x;
  });

  return bestLeft;
}

/**
 * Overhead / top-light width labels sit above the frame. They must NOT be
 * snapped onto the bottom width rulers (that was collapsing all "700"s under the door).
 */
function isTopBandWidthLabel(element, svgEl) {
  if (!element || !svgEl) return false;
  // Explicit left-height tags are never top-band width
  if (element.getAttribute?.('data-height-side') === 'left') return false;
  if (element.getAttribute?.('data-height-part') === '1') return false;
  if (element.getAttribute?.('data-dim-band') === 'top') return true;
  if (element.getAttribute?.('data-dim-axis') === 'height') return false;

  const y = parseFloat(element.getAttribute('y') || 'NaN');
  if (Number.isNaN(y)) return false;

  // Height labels use start/end anchors; overhead widths use middle
  if (element.tagName === 'text') {
    const anchor = element.getAttribute('text-anchor') || 'middle';
    if (anchor === 'start' || anchor === 'end') return false;
  }

  const { y: vbY, height: vbH } = getSvgViewBox(svgEl);
  const frameTop = estimateOuterFrameTop(svgEl);

  // Clearly in the top overhead band (above / near the frame top)
  if (frameTop != null && y <= frameTop + 28) return true;
  return y < vbY + vbH * 0.28;
}

/** Bottom horizontal dimension rulers (sub then main), from actual <line> geometry. */
function collectBottomHorizontalRulerYs(svgEl) {
  if (!svgEl) return [];

  const { y: vbY, height: vbHeight } = getSvgViewBox(svgEl);
  const minY = vbY + vbHeight * 0.5;
  const frameBottom = estimateOuterFrameBottom(svgEl);
  // Only keep lines clearly below the frame so the sill/dorpel is never treated as a ruler
  const belowFrameMin = frameBottom != null ? frameBottom + 10 : minY;

  const raw = [];
  svgEl.querySelectorAll('line').forEach((line) => {
    const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
    const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
    const x1 = parseFloat(line.getAttribute('x1') || '0');
    const x2 = parseFloat(line.getAttribute('x2') || '0');
    if (Number.isNaN(y1) || Number.isNaN(y2)) return;
    if (Math.abs(y1 - y2) > 0.75) return; // not horizontal
    if (Math.abs(x2 - x1) < 40) return; // skip short ticks
    if (y1 < Math.max(minY, belowFrameMin)) return;
    raw.push(y1);
  });

  raw.sort((a, b) => a - b);
  const clustered = [];
  raw.forEach((y) => {
    if (!clustered.length || y - clustered[clustered.length - 1] > 6) {
      clustered.push(y);
    }
  });
  return clustered;
}

function shiftBottomDimensionGeometry(svg, threshold, shift) {
  if (!svg || shift < 1) return;

  const { width: vbW } = getSvgViewBox(svg);
  const rightBand = getSvgViewBox(svg).x + vbW * 0.5;

  const isRightSideHeightLabel = (el) => {
    if (el.getAttribute('data-dim-axis') === 'height') return true;
    if (el.getAttribute('data-height-part') === '1') return true;
    const x = parseFloat(el.getAttribute('x') || 'NaN');
    const w = parseFloat(el.getAttribute('width') || '0');
    if (Number.isNaN(x)) return false;
    // Keep vertical section labels in place when pushing width rulers down
    if (x > rightBand && (el.tagName === 'foreignObject' ? w <= 90 : true)) {
      if (el.tagName === 'text' && (el.getAttribute('text-anchor') || '') === 'start') return true;
      if (el.tagName === 'foreignObject' && w > 0 && w <= 90) return true;
    }
    return false;
  };

  svg.querySelectorAll('line').forEach((line) => {
    const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
    const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
    const x1 = parseFloat(line.getAttribute('x1') || '0');
    const x2 = parseFloat(line.getAttribute('x2') || '0');
    if (Number.isNaN(y1) || Number.isNaN(y2)) return;

    const isHorizontal = Math.abs(y1 - y2) <= 0.75;
    const isShortTick = Math.abs(x1 - x2) <= 0.75 && Math.abs(y1 - y2) <= 24;

    if (isHorizontal && y1 >= threshold) {
      line.setAttribute('y1', String(y1 + shift));
      line.setAttribute('y2', String(y2 + shift));
      return;
    }
    if (isShortTick && Math.min(y1, y2) >= threshold) {
      line.setAttribute('y1', String(y1 + shift));
      line.setAttribute('y2', String(y2 + shift));
    }
  });

  svg.querySelectorAll('foreignObject, text').forEach((el) => {
    if (isRightSideHeightLabel(el)) return;
    const y = parseFloat(el.getAttribute('y') || 'NaN');
    if (!Number.isNaN(y) && y >= threshold) {
      el.setAttribute('y', String(y + shift));
    }
  });
}

function expandViewBoxForBottom(svg, neededBottom) {
  const { x: vbX, y: vbY, width: vbW, height: vbH } = getSvgViewBox(svg);
  if (neededBottom > vbY + vbH) {
    svg.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${neededBottom - vbY}`);
  }
}

/**
 * Push bottom width rulers down so they clear the frame, and keep enough
 * gap between sub-rulers and the main width ruler.
 * Schuifpui live pattern: [frame] → parts above → [sub] → [main] → total below
 * Terrasdeur panels:      [frame] → [sub] → parts below → [main] → total below
 */
export function ensureBottomDimensionClearance(
  svg,
  { minGap = 40, minPartMainGap = 52 } = {}
) {
  if (!svg) return;

  const frameBottom = estimateOuterFrameBottom(svg);
  if (frameBottom == null) return;

  let dimYs = collectBottomHorizontalRulerYs(svg);
  if (!dimYs.length) return;

  // 1) Clear the frame sill
  const frameShift = Math.max(0, frameBottom + minGap - dimYs[0]);
  if (frameShift >= 1) {
    shiftBottomDimensionGeometry(svg, frameBottom + 2, frameShift);
    dimYs = dimYs.map((y) => y + frameShift);
  }

  // 2) Keep room under sub-labels before the main width line
  if (dimYs.length >= 2) {
    const partY = dimYs[0];
    const mainY = dimYs[dimYs.length - 1];
    const partMainShift = Math.max(0, minPartMainGap - (mainY - partY));
    if (partMainShift >= 1) {
      // Only move the main ruler band (and labels under it), leave sub-rulers
      const mainThreshold = (partY + mainY) / 2;
      shiftBottomDimensionGeometry(svg, mainThreshold, partMainShift);
      dimYs[dimYs.length - 1] = mainY + partMainShift;
    }
  }

  expandViewBoxForBottom(svg, dimYs[dimYs.length - 1] + 40);
}

/**
 * Export Quotes: merge broken per-pane bottom width sub-rulers (Schuifpui /
 * Kozijnen equal parts) into one continuous horizontal line with ticks kept.
 *
 * Previously this stripped equal segments entirely; Export then looked gapped.
 * Asymmetric door side-lights (e.g. 300|1400|300) are also unified when they
 * share the same Y — one solid sub-ruler like the live door drawings.
 */
export function stripPartSegmentWidthRulers(svg) {
  if (!svg) return;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const { y: vbY, height: vbH } = getSvgViewBox(svg);
  const minY = vbY + vbH * 0.5;
  const horizontals = [];

  svg.querySelectorAll('line').forEach((line) => {
    const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
    const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
    const x1 = parseFloat(line.getAttribute('x1') || '0');
    const x2 = parseFloat(line.getAttribute('x2') || '0');
    if (Number.isNaN(y1) || Number.isNaN(y2)) return;
    if (Math.abs(y1 - y2) > 0.75) return;
    if (y1 < minY) return;
    const len = Math.abs(x2 - x1);
    if (len < 40) return;
    horizontals.push({
      line,
      y: y1,
      len,
      xMin: Math.min(x1, x2),
      xMax: Math.max(x1, x2),
      stroke: line.getAttribute('stroke') || '#4A4A4A',
      strokeWidth: line.getAttribute('stroke-width') || '1',
    });
  });

  if (horizontals.length < 2) return;

  const maxLen = Math.max(...horizontals.map((h) => h.len));
  // Already a continuous near-full-width sub-ruler — nothing to merge
  const hasContinuousSub = horizontals.some(
    (h) => h.len >= maxLen * 0.55 && h.len < maxLen * 0.995
  );
  if (hasContinuousSub) return;

  const shorts = horizontals.filter((h) => h.len < maxLen * 0.72);
  if (shorts.length < 2) return;

  // Cluster shorts by Y so stacked underlight + part rulers stay separate
  shorts.sort((a, b) => a.y - b.y || a.xMin - b.xMin);
  const yClusters = [];
  shorts.forEach((seg) => {
    const last = yClusters[yClusters.length - 1];
    if (!last || Math.abs(seg.y - last.y) > 3) {
      yClusters.push({ y: seg.y, segs: [seg] });
    } else {
      last.segs.push(seg);
    }
  });

  yClusters.forEach((cluster) => {
    if (cluster.segs.length < 2) return;

    const xMin = Math.min(...cluster.segs.map((s) => s.xMin));
    const xMax = Math.max(...cluster.segs.map((s) => s.xMax));
    const span = xMax - xMin;
    if (span < 40) return;

    // Require segments to cover most of the span (real part sub-ruler, not noise)
    const covered = cluster.segs.reduce((sum, s) => sum + s.len, 0);
    if (covered < span * 0.55) return;

    const template = cluster.segs[0];
    const parent = template.line.parentNode || svg;
    const merged = document.createElementNS(SVG_NS, 'line');
    merged.setAttribute('x1', String(xMin));
    merged.setAttribute('x2', String(xMax));
    merged.setAttribute('y1', String(cluster.y));
    merged.setAttribute('y2', String(cluster.y));
    merged.setAttribute('stroke', template.stroke);
    merged.setAttribute('stroke-width', template.strokeWidth);
    merged.setAttribute('stroke-linecap', 'square');
    parent.insertBefore(merged, template.line);

    cluster.segs.forEach(({ line }) => line.remove());
  });
}

function getElementAnchorX(element) {
  if (!element) return 0;
  if (element.tagName === 'foreignObject') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const w = parseFloat(element.getAttribute('width') || '0');
    return x + w / 2;
  }
  return parseFloat(element.getAttribute('x') || '0');
}

/** Right-side vertical dimension rulers (sub then main), from actual <line> geometry. */
function collectRightVerticalRulerXs(svgEl) {
  if (!svgEl) return [];

  const { x: vbX, width: vbWidth } = getSvgViewBox(svgEl);
  const minX = vbX + vbWidth * 0.45;

  const raw = [];
  svgEl.querySelectorAll('line').forEach((line) => {
    const x1 = parseFloat(line.getAttribute('x1') || 'NaN');
    const x2 = parseFloat(line.getAttribute('x2') || 'NaN');
    const y1 = parseFloat(line.getAttribute('y1') || '0');
    const y2 = parseFloat(line.getAttribute('y2') || '0');
    if (Number.isNaN(x1) || Number.isNaN(x2)) return;
    if (Math.abs(x1 - x2) > 0.75) return; // not vertical
    if (Math.abs(y2 - y1) < 30) return; // skip short ticks
    if (x1 < minX) return;
    raw.push(x1);
  });

  raw.sort((a, b) => a - b);
  const clustered = [];
  raw.forEach((x) => {
    if (!clustered.length || x - clustered[clustered.length - 1] > 8) {
      clustered.push(x);
    }
  });
  return clustered;
}

/** Left-side vertical dimension rulers (side-light height), from actual <line> geometry.
 *  Only keep lines in the LEFT GUTTER (at/left of the outer frame) — never mullions
 *  inside the side light, which would park labels on top of the glass.
 */
function collectLeftVerticalRulerXs(svgEl) {
  if (!svgEl) return [];

  const { x: vbX, width: vbWidth } = getSvgViewBox(svgEl);
  const maxX = vbX + vbWidth * 0.45;
  const frameLeft = estimateOuterFrameLeft(svgEl);
  // Side-light height ruler sits just left of the glass (~30px left of glassX),
  // often a bit inside the outer frame x — keep a generous gutter, still exclude mullions
  const gutterMax = frameLeft != null ? frameLeft + 45 : maxX;

  const raw = [];
  svgEl.querySelectorAll('line').forEach((line) => {
    const x1 = parseFloat(line.getAttribute('x1') || 'NaN');
    const x2 = parseFloat(line.getAttribute('x2') || 'NaN');
    const y1 = parseFloat(line.getAttribute('y1') || '0');
    const y2 = parseFloat(line.getAttribute('y2') || '0');
    if (Number.isNaN(x1) || Number.isNaN(x2)) return;
    if (Math.abs(x1 - x2) > 0.75) return;
    if (Math.abs(y2 - y1) < 30) return;
    if (x1 > maxX) return;
    if (x1 > gutterMax) return;
    raw.push(x1);
  });

  raw.sort((a, b) => a - b);
  const clustered = [];
  raw.forEach((x) => {
    if (!clustered.length || x - clustered[clustered.length - 1] > 8) {
      clustered.push(x);
    }
  });
  return clustered;
}

function isLeftHeightElement(element) {
  if (!element) return false;
  if (element.getAttribute?.('data-height-side') === 'left') return true;
  // Top-light / overhead width labels must never be treated as left height
  if (element.getAttribute?.('data-dim-band') === 'top') return false;
  if (element.getAttribute?.('text-anchor') === 'end') return true;
  if (element.tagName === 'foreignObject') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    const w = parseFloat(element.getAttribute('width') || '0');
    const svg = element.ownerSVGElement || element.closest?.('svg');
    if (!svg || !(w > 0 && w <= 90)) return false;
    if (isTopBandWidthLabel(element, svg)) return false;
    const { x: vbX, width: vbW } = getSvgViewBox(svg);
    return x < vbX + vbW * 0.45 && element.getAttribute('data-dim-axis') === 'height';
  }
  if (element.tagName === 'text') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const svg = element.ownerSVGElement || element.closest?.('svg');
    if (!svg) return false;
    if (isTopBandWidthLabel(element, svg)) return false;
    const { x: vbX, width: vbW } = getSvgViewBox(svg);
    if (x >= vbX + vbW * 0.45) return false;
    if (element.getAttribute('data-dim-axis') === 'height') return true;
    // Recover mis-tagged left height labels from older saved SVGs
    if (element.getAttribute('data-dim-axis') === 'width') {
      const y = parseFloat(element.getAttribute('y') || '0');
      const frameBottom = estimateOuterFrameBottom(svg);
      const frameTop = estimateOuterFrameTop(svg);
      // Bottom width band
      if (frameBottom != null && y > frameBottom + 6) return false;
      // Top-light / overhead width band (e.g. voordeur "500")
      if (frameTop != null && y <= frameTop + 28) return false;
      const { y: vbY, height: vbH } = getSvgViewBox(svg);
      if (y < vbY + vbH * 0.36) return false;
      const leftRulers = collectLeftVerticalRulerXs(svg);
      if (!leftRulers.length) return false;
      const near = leftRulers.some((rx) => Math.abs(rx - x) < 80);
      return near;
    }
  }
  return false;
}

function getElementCenterY(element) {
  if (!element) return 0;
  if (element.tagName === 'foreignObject') {
    const y = parseFloat(element.getAttribute('y') || '0');
    const h = parseFloat(element.getAttribute('height') || '0');
    return y + h / 2;
  }
  return parseFloat(element.getAttribute('y') || '0');
}

/**
 * Rightmost vertical columns are the real dimension rulers.
 * Earlier columns are often the door/frame edge — do not treat those as the sub-ruler.
 *   … frame | sub —— [661] —— main —— [2200]
 */
function resolveHeightRulerColumns(rulerXs) {
  if (!rulerXs?.length) return null;
  const mainX = rulerXs[rulerXs.length - 1];
  const partX = rulerXs.length >= 2 ? rulerXs[rulerXs.length - 2] : mainX;
  return { partX, mainX };
}

/**
 * Place height labels on vertical ruler columns:
 *   sub column —— [661] —— main column —— [2200]
 * Section labels sit in the gutter BETWEEN the two rulers (never on the sub line).
 * Short segments (e.g. small parapet) keep the label inset from both tick marks.
 */
function findVerticalSegmentBounds(svgEl, nearX, nearY) {
  if (!svgEl) return null;
  let best = null;
  svgEl.querySelectorAll('line').forEach((line) => {
    const x1 = parseFloat(line.getAttribute('x1') || 'NaN');
    const x2 = parseFloat(line.getAttribute('x2') || 'NaN');
    const y1 = parseFloat(line.getAttribute('y1') || '0');
    const y2 = parseFloat(line.getAttribute('y2') || '0');
    if (Number.isNaN(x1) || Number.isNaN(x2)) return;
    if (Math.abs(x1 - x2) > 1.25) return;
    if (Math.abs(x1 - nearX) > 28) return;
    const yMin = Math.min(y1, y2);
    const yMax = Math.max(y1, y2);
    const len = yMax - yMin;
    if (len < 12) return;
    // Prefer the shortest vertical that still contains the label (parapet vs full height)
    if (nearY < yMin - 6 || nearY > yMax + 6) return;
    if (!best || len < best.len) {
      best = { yMin, yMax, len, x: x1 };
    }
  });
  return best;
}

function positionHeightLabelOnRulers(
  element,
  svgEl,
  imgEl,
  wrapEl,
  isPartHeight,
  rulerXs
) {
  const cols = resolveHeightRulerColumns(rulerXs);
  if (!cols) return null;

  const { partX, mainX } = cols;
  const gutter = Math.max(0, mainX - partX);

  let labelX;
  let align = 'start';
  let tier = 'height';

  if (isPartHeight) {
    if (rulerXs.length >= 2 && gutter >= 16) {
      // Center of the space between sub-ruler and main ruler
      labelX = partX + gutter / 2;
      align = 'center';
      tier = 'height-part';
    } else {
      // Fallback when columns are tight / only one detected — sit clearly right of the line
      labelX = partX + Math.max(28, gutter * 0.55 || 28);
      align = 'center';
      tier = 'height-part';
    }
  } else {
    labelX = mainX + 12;
  }

  let labelY = getElementCenterY(element);

  // Keep short vertical sub-labels clear of the top/bottom tick marks
  if (isPartHeight && svgEl) {
    const bounds = findVerticalSegmentBounds(svgEl, partX, labelY);
    if (bounds) {
      const edgePad = 10;
      const halfText = 6;
      const minCenter = bounds.yMin + edgePad + halfText;
      const maxCenter = bounds.yMax - edgePad - halfText;
      if (minCenter <= maxCenter) {
        labelY = Math.min(maxCenter, Math.max(minCenter, labelY));
      } else {
        // Segment shorter than the label — keep true center and push further right
        labelY = (bounds.yMin + bounds.yMax) / 2;
        labelX += 10;
      }
      // Extra horizontal clearance when the segment is visually short (e.g. parapet 250)
      if (bounds.len < 56) {
        labelX += 4;
      }
    }
  }

  const pt =
    imgEl?.clientWidth > 0
      ? svgPointToImageWrap(svgEl, imgEl, wrapEl, labelX, labelY)
      : svgPointToWrap(svgEl, wrapEl, labelX, labelY);

  if (!pt) return null;
  return {
    ...pt,
    align,
    tier,
  };
}

/**
 * Place left side-light height labels LEFT of the left vertical ruler.
 * Keeps segment mid-Y so glass (1537) and parapet (600) stay on their own segments.
 * Anchor = point left of the ruler; CSS/PDF use end-align (translate -100%) so
 * the label sits entirely outside the drawing — never on the glass/parapet.
 */
function positionHeightLabelOnLeftRulers(
  element,
  svgEl,
  imgEl,
  wrapEl,
  rulerXs
) {
  if (!rulerXs?.length) return null;

  // Leftmost gutter ruler = real height dimension line (not a mullion inside the panel)
  const rulerX = rulerXs[0];
  let labelY = getElementCenterY(element);
  // Clearance left of the rule so text does not sit on the side-light drawing
  const LABEL_GAP = 28;
  let labelX = rulerX - LABEL_GAP;

  const bounds = findVerticalSegmentBounds(svgEl, rulerX, labelY);
  if (bounds) {
    labelX = bounds.x - LABEL_GAP;
    const edgePad = 10;
    const halfText = 6;
    const minCenter = bounds.yMin + edgePad + halfText;
    const maxCenter = bounds.yMax - edgePad - halfText;
    if (minCenter <= maxCenter) {
      labelY = Math.min(maxCenter, Math.max(minCenter, labelY));
    } else {
      labelY = (bounds.yMin + bounds.yMax) / 2;
      labelX = bounds.x - (LABEL_GAP + 6);
    }
    if (bounds.len < 56) {
      labelX = Math.min(labelX, bounds.x - (LABEL_GAP + 6));
    }
  }

  // Never let the anchor drift into the product — keep it left of the outer frame
  const frameLeft = estimateOuterFrameLeft(svgEl);
  if (frameLeft != null) {
    labelX = Math.min(labelX, frameLeft - LABEL_GAP);
  }

  const pt =
    imgEl?.clientWidth > 0
      ? svgPointToImageWrap(svgEl, imgEl, wrapEl, labelX, labelY)
      : svgPointToWrap(svgEl, wrapEl, labelX, labelY);

  if (!pt) return null;
  return {
    ...pt,
    align: 'end',
    // Dedicated tier so we never inherit height-part's translate(-50%) centering
    tier: 'height-part-left',
  };
}

/**
 * Place width labels on bottom rulers (quote / PDF layout).
 *   Kozijnen (room above sub-ruler): part widths ABOVE the sub-ruler
 *   Schuifpui (tight ~14px band):     part widths BELOW the sub-ruler
 *   Terrasdeur panels:                part widths BELOW the sub-ruler
 *   Total width:                      BELOW main ruler
 */
function positionWidthLabelOnRulers(
  element,
  svgEl,
  imgEl,
  wrapEl,
  isPartWidth,
  rulerYs
) {
  const anchorX = getElementAnchorX(element);
  if (!rulerYs.length) return null;

  const mainLineY = rulerYs[rulerYs.length - 1];
  const partLineY = rulerYs.length >= 2 ? rulerYs[0] : mainLineY;

  const PART_ABOVE_GAP = 12;
  const PART_BELOW_GAP = 10;
  const BELOW_GAP = 12;
  // Need this much air between sill and sub-ruler to fit a label above the line
  const MIN_GAP_FOR_LABEL_ABOVE = 30;

  let svgY;
  let tier = isPartWidth ? 'part' : 'main';

  if (isPartWidth) {
    const nativeY = getElementCenterY(element);
    const frameBottom = estimateOuterFrameBottom(svgEl);
    const gapFrameToPart =
      frameBottom != null ? partLineY - frameBottom : Number.POSITIVE_INFINITY;
    // Schuifpui sub-ruler sits ~14px under the sill — placing labels ABOVE
    // parks them on the bottom frame. Force BELOW whenever the band is tight.
    const roomForAbove = gapFrameToPart >= MIN_GAP_FOR_LABEL_ABOVE;
    const preferAbove = roomForAbove && nativeY <= partLineY + 2;
    if (preferAbove) {
      svgY = partLineY - PART_ABOVE_GAP;
      tier = 'part'; // translate(-50%, -100%) — bottom of text just above the line
      if (frameBottom != null && svgY < frameBottom + 8) {
        svgY = Math.max(frameBottom + 10, partLineY - PART_ABOVE_GAP);
      }
    } else {
      svgY = partLineY + PART_BELOW_GAP;
      tier = 'part-below'; // translate(-50%, 0) — top of text just below the line
      if (frameBottom != null) {
        svgY = Math.max(svgY, frameBottom + 14);
      }
    }
  } else {
    svgY = mainLineY + BELOW_GAP;
  }

  const pt =
    imgEl?.clientWidth > 0
      ? svgPointToImageWrap(svgEl, imgEl, wrapEl, anchorX, svgY)
      : svgPointToWrap(svgEl, wrapEl, anchorX, svgY);

  if (!pt) return null;
  return {
    ...pt,
    align: 'center',
    tier,
  };
}

/**
 * Overhead width labels: sit ABOVE the top sub-ruler (like bottom 333/333/334),
 * so the horizontal line stays solid with no gaps through the numbers.
 */
function positionTopBandWidthLabel(element, svgEl, imgEl, wrapEl, isPartWidth) {
  const anchorX = getElementAnchorX(element);
  const nativeY = getElementCenterY(element);
  const PART_ABOVE_GAP = 14;

  // Prefer the nearby top horizontal ruler as the shared baseline
  let rulerY = null;
  svgEl.querySelectorAll('line').forEach((line) => {
    const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
    const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
    const x1 = parseFloat(line.getAttribute('x1') || '0');
    const x2 = parseFloat(line.getAttribute('x2') || '0');
    if (Number.isNaN(y1) || Number.isNaN(y2)) return;
    if (Math.abs(y1 - y2) > 0.75) return;
    if (Math.abs(x2 - x1) < 40) return;
    if (y1 < nativeY - 36 || y1 > nativeY + 36) return;
    if (rulerY == null || Math.abs(y1 - nativeY) < Math.abs(rulerY - nativeY)) {
      rulerY = y1;
    }
  });

  const svgY =
    rulerY != null
      ? isPartWidth
        ? rulerY - PART_ABOVE_GAP
        : rulerY + 12
      : nativeY - PART_ABOVE_GAP;

  const pt =
    imgEl?.clientWidth > 0
      ? svgPointToImageWrap(svgEl, imgEl, wrapEl, anchorX, svgY)
      : svgPointToWrap(svgEl, wrapEl, anchorX, svgY);

  if (!pt) return null;
  return {
    ...pt,
    align: 'center',
    // 'part' = translate(-50%, -100%) so text sits fully above the anchor
    tier: isPartWidth ? 'part' : 'main',
  };
}

function isPartWidthElement(element) {
  if (!element) return false;
  if (element.tagName === 'foreignObject') {
    return isPerPartForeignObject(element);
  }
  const foW = parseFloat(element.getAttribute('data-fo-width') || '0');
  if (foW > 0 && foW <= 55) return true;
  return false;
}

function elementPositionOnImage(element, svgEl, imgEl, wrapEl, axis, options = {}) {
  // Top-light / overhead widths first — never route them through left-height gutter logic
  if (isTopBandWidthLabel(element, svgEl)) {
    const positioned = positionTopBandWidthLabel(
      element,
      svgEl,
      imgEl,
      wrapEl,
      options.isPartWidth ?? true
    );
    if (positioned) return positioned;
  }

  if (axis === 'height') {
    if (isLeftHeightElement(element) || options.isLeftHeight) {
      const leftRulers = options.leftRulerXs || collectLeftVerticalRulerXs(svgEl);
      if (leftRulers.length) {
        const positioned = positionHeightLabelOnLeftRulers(
          element,
          svgEl,
          imgEl,
          wrapEl,
          leftRulers
        );
        if (positioned) return positioned;
      }
    }
    const rulers = options.rulerXs || collectRightVerticalRulerXs(svgEl);
    if (rulers.length) {
      const positioned = positionHeightLabelOnRulers(
        element,
        svgEl,
        imgEl,
        wrapEl,
        options.isPartHeight,
        rulers
      );
      if (positioned) return positioned;
    }
  }

  if (axis === 'width') {
    if (isTopBandWidthLabel(element, svgEl)) {
      const positioned = positionTopBandWidthLabel(
        element,
        svgEl,
        imgEl,
        wrapEl,
        options.isPartWidth
      );
      if (positioned) return positioned;
    }
    const rulers = options.rulerYs || collectBottomHorizontalRulerYs(svgEl);
    if (rulers.length) {
      const positioned = positionWidthLabelOnRulers(
        element,
        svgEl,
        imgEl,
        wrapEl,
        options.isPartWidth,
        rulers
      );
      if (positioned) return positioned;
    }
  }

  if (element.tagName === 'foreignObject') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    const w = parseFloat(element.getAttribute('width') || '0');
    const h = parseFloat(element.getAttribute('height') || '0');
    if (axis === 'height') {
      const leftish = isLeftHeightElement(element) || options.isLeftHeight;
      if (leftish) {
        const frameLeft = estimateOuterFrameLeft(svgEl);
        const anchorX = frameLeft != null ? frameLeft - 28 : x - 8;
        const pt = svgPointToImageWrap(svgEl, imgEl, wrapEl, anchorX, y + h / 2);
        return pt ? { ...pt, align: 'end', tier: 'height-part-left' } : null;
      }
      const pt = svgPointToImageWrap(svgEl, imgEl, wrapEl, x + 4, y + h / 2);
      return pt ? { ...pt, align: 'start', tier: 'height' } : null;
    }
    const pt = svgPointToImageWrap(svgEl, imgEl, wrapEl, x + w / 2, y + h + 2);
    return pt
      ? { ...pt, align: 'center', tier: options.isPartWidth || w <= 55 ? 'part' : 'main' }
      : null;
  }

  if (element.tagName === 'text') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    if (axis === 'height') {
      const leftish = isLeftHeightElement(element) || options.isLeftHeight;
      if (leftish) {
        const frameLeft = estimateOuterFrameLeft(svgEl);
        const anchorX = frameLeft != null ? frameLeft - 28 : x;
        const pt = svgPointToImageWrap(svgEl, imgEl, wrapEl, anchorX, y);
        return pt ? { ...pt, align: 'end', tier: 'height-part-left' } : null;
      }
      const pt = svgPointToImageWrap(svgEl, imgEl, wrapEl, x, y);
      return pt ? { ...pt, align: 'start', tier: 'height' } : null;
    }
    const pt = svgPointToImageWrap(svgEl, imgEl, wrapEl, x, y + 14);
    return pt
      ? { ...pt, align: 'center', tier: options.isPartWidth ? 'part' : 'main' }
      : null;
  }

  return null;
}

function elementPositionOnWrap(element, svgEl, wrapEl, axis, options = {}) {
  if (isTopBandWidthLabel(element, svgEl)) {
    const positioned = positionTopBandWidthLabel(
      element,
      svgEl,
      null,
      wrapEl,
      options.isPartWidth ?? true
    );
    if (positioned) return positioned;
  }

  if (axis === 'height') {
    if (isLeftHeightElement(element) || options.isLeftHeight) {
      const leftRulers = options.leftRulerXs || collectLeftVerticalRulerXs(svgEl);
      if (leftRulers.length) {
        const positioned = positionHeightLabelOnLeftRulers(
          element,
          svgEl,
          null,
          wrapEl,
          leftRulers
        );
        if (positioned) return positioned;
      }
    }
    const rulers = options.rulerXs || collectRightVerticalRulerXs(svgEl);
    if (rulers.length) {
      const positioned = positionHeightLabelOnRulers(
        element,
        svgEl,
        null,
        wrapEl,
        options.isPartHeight,
        rulers
      );
      if (positioned) return positioned;
    }
  }

  if (axis === 'width') {
    if (isTopBandWidthLabel(element, svgEl)) {
      const positioned = positionTopBandWidthLabel(
        element,
        svgEl,
        null,
        wrapEl,
        options.isPartWidth
      );
      if (positioned) return positioned;
    }
    const rulers = options.rulerYs || collectBottomHorizontalRulerYs(svgEl);
    if (rulers.length) {
      const positioned = positionWidthLabelOnRulers(
        element,
        svgEl,
        null,
        wrapEl,
        options.isPartWidth,
        rulers
      );
      if (positioned) return positioned;
    }
  }

  if (element.tagName === 'foreignObject') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    const w = parseFloat(element.getAttribute('width') || '0');
    const h = parseFloat(element.getAttribute('height') || '0');
    if (axis === 'height') {
      const leftish = isLeftHeightElement(element) || options.isLeftHeight;
      if (leftish) {
        const frameLeft = estimateOuterFrameLeft(svgEl);
        const anchorX = frameLeft != null ? frameLeft - 28 : x - 8;
        const pt = svgPointToWrap(svgEl, wrapEl, anchorX, y + h / 2);
        return pt ? { ...pt, align: 'end', tier: 'height-part-left' } : null;
      }
      const pt = svgPointToWrap(svgEl, wrapEl, x + 4, y + h / 2);
      return pt ? { ...pt, align: 'start', tier: 'height' } : null;
    }
    const pt = svgPointToWrap(svgEl, wrapEl, x + w / 2, y + h + 2);
    return pt
      ? { ...pt, align: 'center', tier: options.isPartWidth || w <= 55 ? 'part' : 'main' }
      : null;
  }

  if (element.tagName === 'text') {
    const x = parseFloat(element.getAttribute('x') || '0');
    const y = parseFloat(element.getAttribute('y') || '0');
    if (axis === 'height') {
      const leftish = isLeftHeightElement(element) || options.isLeftHeight;
      if (leftish) {
        const frameLeft = estimateOuterFrameLeft(svgEl);
        const anchorX = frameLeft != null ? frameLeft - 28 : x;
        const pt = svgPointToWrap(svgEl, wrapEl, anchorX, y);
        return pt ? { ...pt, align: 'end', tier: 'height-part-left' } : null;
      }
      const pt = svgPointToWrap(svgEl, wrapEl, x, y);
      return pt ? { ...pt, align: 'start', tier: 'height' } : null;
    }
    const pt = svgPointToWrap(svgEl, wrapEl, x, y + 14);
    return pt
      ? { ...pt, align: 'center', tier: options.isPartWidth ? 'part' : 'main' }
      : null;
  }

  return null;
}

function collectMeasuredCandidates(svgEl) {
  const candidates = [];

  svgEl.querySelectorAll('foreignObject').forEach((foreignObject, index) => {
    const text = getForeignObjectLabel(foreignObject);
    if (!text || !/^\d+$/.test(text)) return;

    const isDim =
      isMainDimensionForeignObject(foreignObject) ||
      isPerPartForeignObject(foreignObject) ||
      !!foreignObject.querySelector('input[type="number"]') ||
      inferLabelAxis(foreignObject) === 'height';
    if (!isDim) return;

    candidates.push({
      id: `fo-${index}`,
      text,
      element: foreignObject,
      axis: inferLabelAxis(foreignObject),
      isPartWidth: isPartWidthElement(foreignObject),
    });
  });

  svgEl.querySelectorAll('text').forEach((textEl, index) => {
    if (!isDimensionLabelText(textEl)) return;

    const text = getTextLabel(textEl);
    if (!text) return;

    candidates.push({
      id: `text-${index}`,
      text,
      element: textEl,
      axis: inferLabelAxis(textEl),
      isPartWidth: isPartWidthElement(textEl),
    });
  });

  return candidates;
}

function resolveElementPosition(element, svgEl, wrapEl, imgEl, axis, options = {}) {
  if (imgEl && imgEl.clientWidth > 0) {
    return elementPositionOnImage(element, svgEl, imgEl, wrapEl, axis, options);
  }
  return elementPositionOnWrap(element, svgEl, wrapEl, axis, options);
}

function buildHeuristicOverlays(svgEl, wrapEl, configWidth, configHeight, imgEl = null) {
  const widthText = formatDrawingDimension(configWidth);
  const heightText = formatDrawingDimension(configHeight);
  if (!widthText && !heightText) return [];

  const wrapRect = wrapEl.getBoundingClientRect();
  let offsetX;
  let offsetY;
  let drawWidth;
  let drawHeight;

  if (imgEl && imgEl.clientWidth > 0) {
    const imgRect = imgEl.getBoundingClientRect();
    offsetX = imgRect.left - wrapRect.left;
    offsetY = imgRect.top - wrapRect.top;
    if (svgEl) {
      const { padX, padY, renderedWidth, renderedHeight } = getSvgMeetTransform(svgEl, imgEl);
      offsetX += padX;
      offsetY += padY;
      drawWidth = renderedWidth;
      drawHeight = renderedHeight;
    } else {
      drawWidth = imgRect.width;
      drawHeight = imgRect.height;
    }
  } else {
    const svgRect = svgEl?.getBoundingClientRect();
    if (!svgRect || wrapRect.width < 1 || svgRect.width < 1) return [];
    offsetX = svgRect.left - wrapRect.left;
    offsetY = svgRect.top - wrapRect.top;
    drawWidth = svgRect.width;
    drawHeight = svgRect.height;
  }

  const overlays = [];

  if (widthText) {
    overlays.push({
      id: 'dim-width',
      text: widthText,
      left: offsetX + drawWidth * 0.5,
      top: offsetY + drawHeight * 0.97,
      align: 'center',
      tier: 'main',
    });
  }

  if (heightText) {
    overlays.push({
      id: 'dim-height',
      text: heightText,
      left: offsetX + drawWidth * 0.9,
      top: offsetY + drawHeight * 0.5,
      align: 'start',
      tier: 'height',
    });
  }

  return overlays;
}

function isHeightDimensionOverlay(overlay) {
  if (overlay?.id === 'dim-height') return true;
  const tier = overlay?.tier || '';
  return tier === 'height' || tier === 'height-part' || tier === 'height-part-left';
}

function applyDimensionOverlayOptions(overlays, options = {}) {
  if (!options.hideHeight) return overlays;
  return overlays.filter((overlay) => !isHeightDimensionOverlay(overlay));
}

/**
 * Build crisp HTML overlays for EVERY dimension label in the drawing.
 * Width labels are anchored to the real SVG ruler lines so they never sit on a divider.
 */
export function measureLiveDimensionOverlays(
  wrapEl,
  svgEl,
  configWidth,
  configHeight,
  imgEl = null,
  options = {}
) {
  const { hideHeight = false } = options;
  if (!wrapEl || !svgEl) return [];

  const widthText = formatDrawingDimension(configWidth);
  const heightText = formatDrawingDimension(configHeight);
  const rulerYs = collectBottomHorizontalRulerYs(svgEl);
  const rulerXs = collectRightVerticalRulerXs(svgEl);
  const leftRulerXs = collectLeftVerticalRulerXs(svgEl);
  const candidates = collectMeasuredCandidates(svgEl);
  const overlays = [];

  if (candidates.length) {
    candidates.forEach((candidate) => {
      const axis = candidate.axis || 'width';
      if (hideHeight && axis === 'height') return;
      const isLeftHeight = axis === 'height' && isLeftHeightElement(candidate.element);
      // Overall height is never a section/part label — even if a narrow FO was
      // mis-tagged data-height-part (Dakkapel main height FO is 72px).
      const matchesOverallHeight = Boolean(heightText && candidate.text === heightText);
      const isPartHeight =
        axis === 'height' &&
        !matchesOverallHeight &&
        (isLeftHeight ||
          candidate.element.getAttribute?.('data-height-part') === '1' ||
          (heightText && candidate.text !== heightText));
      const isPartWidth =
        axis === 'width' &&
        (candidate.isPartWidth || (widthText && candidate.text !== widthText));

      const position = resolveElementPosition(candidate.element, svgEl, wrapEl, imgEl, axis, {
        isPartWidth,
        isPartHeight,
        isLeftHeight,
        rulerYs,
        rulerXs,
        leftRulerXs,
      });
      if (!position) return;

      overlays.push({
        id: candidate.id,
        text: candidate.text,
        left: position.left,
        top: position.top,
        align: position.align,
        tier:
          position.tier ||
          (isPartWidth
            ? 'part'
            : isLeftHeight
              ? 'height-part-left'
              : isPartHeight
                ? 'height-part'
                : axis === 'height'
                  ? 'height'
                  : 'main'),
      });
    });
  }

  const heuristics = buildHeuristicOverlays(svgEl, wrapEl, configWidth, configHeight, imgEl);
  if (widthText && !overlays.some((o) => o.text === widthText && o.align === 'center')) {
    if (rulerYs.length && imgEl?.clientWidth > 0) {
      const mainY = rulerYs[rulerYs.length - 1] + 12;
      const vb = svgEl.viewBox?.baseVal;
      const midX = (vb?.x || 0) + (vb?.width || 800) / 2;
      const pt = svgPointToImageWrap(svgEl, imgEl, wrapEl, midX, mainY);
      if (pt) {
        overlays.push({
          id: 'dim-width',
          text: widthText,
          left: pt.left,
          top: pt.top,
          align: 'center',
          tier: 'main',
        });
      } else {
        const hw = heuristics.find((o) => o.id === 'dim-width');
        if (hw) overlays.push({ ...hw, tier: 'main' });
      }
    } else {
      const hw = heuristics.find((o) => o.id === 'dim-width');
      if (hw) overlays.push({ ...hw, tier: 'main' });
    }
  }
  if (
    !hideHeight &&
    heightText &&
    !overlays.some((o) => o.text === heightText && isHeightDimensionOverlay(o))
  ) {
    const hh = heuristics.find((o) => o.id === 'dim-height');
    if (hh) overlays.push({ ...hh, tier: 'height' });
  }

  if (!overlays.length) {
    return applyDimensionOverlayOptions(heuristics, options);
  }

  return applyDimensionOverlayOptions(overlays, options);
}

export function stripSvgDimensionLabels(svg) {
  if (!svg) return;

  svg.querySelectorAll('foreignObject').forEach((foreignObject) => {
    if (
      isMainDimensionForeignObject(foreignObject) ||
      isPerPartForeignObject(foreignObject) ||
      foreignObject.querySelector('input[type="number"]') ||
      /^\d+$/.test(stripMmFromLabel(foreignObject.textContent || ''))
    ) {
      foreignObject.remove();
    }
  });

  svg.querySelectorAll('text, tspan').forEach((textEl) => {
    if (textEl.getAttribute('data-dimension-label') === 'true') {
      textEl.remove();
      return;
    }
    const content = stripMmFromLabel(textEl.textContent || '');
    // Remove any pure numeric dimension mark so it cannot show through in the PDF image
    if (/^\d+$/.test(content)) {
      textEl.remove();
    }
  });
}

/**
 * Move section-height labels into the gutter between the sub and main vertical rulers.
 * Call after FO→text (and after ruler unify when available).
 */
export function repositionHeightPartLabelsBetweenRulers(svg) {
  if (!svg) return;

  const cols = resolveHeightRulerColumns(collectRightVerticalRulerXs(svg));
  if (!cols) return;

  const { partX, mainX } = cols;
  const gutter = Math.max(0, mainX - partX);
  if (gutter < 12) return;

  const labelX = partX + gutter / 2;

  svg.querySelectorAll('text[data-height-part="1"]').forEach((textEl) => {
    // Left side-light labels must stay on the left column
    if (textEl.getAttribute('data-height-side') === 'left') return;
    if (textEl.getAttribute('text-anchor') === 'end') return;
    textEl.setAttribute('x', String(labelX));
    textEl.setAttribute('text-anchor', 'middle');
  });
}

export function getDrawingDimFontPx(compact = false) {
  return compact ? DRAWING_DIM_FONT_PX_COMPACT : DRAWING_DIM_FONT_PX;
}

export { DRAWING_DIM_FONT_PX, DRAWING_DIM_FONT_PX_COMPACT };
